const round2 = (value) => Math.round(Number(value) * 100) / 100;

const amountOrBlank = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) && amount > 0 ? round2(amount) : '';
};

export const BOOKING_PROPOSAL_CONTRACT_VERSION = '1.0';

export function normalizeBookingProposal(proposal, context = {}) {
    if (!proposal) return null;
    // No hardcoded bank leg: the caller resolves it from the bank-account
    // mapping or the primary_bank chart role. A missing leg surfaces as an
    // empty-account row plus a warning — visible and fixable, never a
    // silently substituted number.
    const rows = rowsFromBookingSuggestion({
        suggestion: proposal,
        transaction: context.transaction || { amount: 0 },
        defaultLegAccount: context.defaultLegAccount || { number: '', name: '' },
        accountNameFor: context.accountNameFor,
    });
    const warnings = Array.isArray(proposal.warnings) ? [...proposal.warnings] : [];
    if (rows.some((row) => !row.account_number)) {
        warnings.push(
            'Betalkontoraden saknar konto — koppla bankkontot till din kontoplan eller '
            + 'mappa rollen Företagskonto under Inställningar → Bokföring → Kontoplansroller.'
        );
    }
    const confidence = Number.isFinite(Number(proposal.confidence))
        ? Number(proposal.confidence)
        : null;
    return {
        contract_version: proposal.contract_version || BOOKING_PROPOSAL_CONTRACT_VERSION,
        proposal_id: proposal.proposal_id || null,
        suggestion_group_id: proposal.suggestion_group_id || context.suggestionGroupId || null,
        source: proposal.source || context.source || 'manual',
        strategy: proposal.strategy || proposal.matchStrategy || proposal.source || 'unknown',
        title: proposal.title || bookingSuggestionTitle(proposal),
        reason: proposal.reason || proposal.matchReason || null,
        confidence,
        confidence_tier: proposal.confidence_tier
            || (confidence === null ? 'unknown' : confidence >= 0.9 ? 'high' : confidence >= 0.7 ? 'medium' : 'low'),
        rows,
        evidence: Array.isArray(proposal.evidence) ? proposal.evidence : [],
        warnings,
        template_id: proposal.template_id || null,
        chart_validated: proposal.chart_validated === true,
        automation: proposal.automation || {
            eligible: false,
            requires_explicit_opt_in: true,
            blocked_reasons: ['legacy_proposal'],
        },
        metadata: proposal.metadata || {},
    };
}

export function proposalsFromResponse(payload, context = {}) {
    const candidates = Array.isArray(payload?.proposals) && payload.proposals.length > 0
        ? payload.proposals
        : (Array.isArray(payload?.suggestions) ? payload.suggestions : []);
    return candidates
        .map((proposal) => normalizeBookingProposal(proposal, {
            ...context,
            suggestionGroupId: payload?.suggestion_group_id || context.suggestionGroupId,
        }))
        .filter(Boolean);
}

export function proposalFromTemplateResponse(payload, template = {}, context = {}) {
    if (!payload) return null;
    const candidate = payload.proposal || (Array.isArray(payload.lines) && payload.lines.length > 0
        ? {
            source: 'template',
            strategy: 'template',
            title: template.template_name || 'Bokföringsmall',
            reason: 'Beräknad från vald bokföringsmall',
            confidence: 1,
            template_id: template.template_id || null,
            chart_validated: payload.lines.every((line) => Boolean(line.account_name)),
            warnings: payload.errors || [],
            rows: payload.lines.map((line) => ({
                account_number: line.account,
                account_name: line.account_name || null,
                side: line.side,
                amount: line.amount,
                description: line.transaction_description || null,
            })),
        }
        : null);
    return normalizeBookingProposal(candidate, context);
}

export function journalEntriesFromProposal(proposal, fallbackDescription = '') {
    return (proposal?.rows || []).map((row) => ({
        account: String(row.account_number || ''),
        accountName: row.account_name || '',
        debit: Number(row.debit) || 0,
        credit: Number(row.credit) || 0,
        description: row.description || fallbackDescription,
    }));
}

export function proposalRowsFromJournalEntries(entries = []) {
    return entries.map((entry) => ({
        account_number: String(entry.account || entry.account_number || ''),
        account_name: entry.accountName || entry.account_name || '',
        debit: Number(entry.debit) || 0,
        credit: Number(entry.credit) || 0,
        description: entry.description || null,
    }));
}

/**
 * Convert bank, Skatteverket and rule/history suggestion shapes into the one
 * editable row contract used by the booking modal.
 */
export function rowsFromBookingSuggestion({
    suggestion,
    transaction,
    defaultLegAccount,
    accountNameFor = () => '',
}) {
    if (!suggestion || !transaction) return [];

    if (Array.isArray(suggestion.rows) && suggestion.rows.length >= 2) {
        return suggestion.rows.map((row) => {
            const number = String(row.account_number || row.account || '');
            const sideAmount = amountOrBlank(row.amount);
            return {
                account_number: number,
                account_name: row.account_name || accountNameFor(number) || '',
                debit: amountOrBlank(row.debit) || (row.side === 'debit' ? sideAmount : ''),
                credit: amountOrBlank(row.credit) || (row.side === 'credit' ? sideAmount : ''),
                description: row.description || row.transaction_description || null,
            };
        });
    }

    const amount = round2(Math.abs(Number(transaction.amount)));

    // Skatteverket defaults describe the accounting sides explicitly. Do not
    // reinterpret them using the bank sign—the payment/refund type already
    // selected the correct debit and credit accounts on the backend.
    if (suggestion.debitAccount && suggestion.creditAccount) {
        const debitNumber = String(suggestion.debitAccount);
        const creditNumber = String(suggestion.creditAccount);
        return [
            {
                account_number: debitNumber,
                account_name: accountNameFor(debitNumber) || '',
                debit: amount,
                credit: '',
                description: suggestion.description || null,
            },
            {
                account_number: creditNumber,
                account_name: accountNameFor(creditNumber) || '',
                debit: '',
                credit: amount,
                description: suggestion.description || null,
            },
        ];
    }

    // Legacy bank suggestion: positive statement amount means money in.
    const incoming = Number(transaction.amount) > 0;
    const legNumber = String(suggestion.bankAccount || defaultLegAccount.number);
    const counterNumber = suggestion.counterAccount ? String(suggestion.counterAccount) : '';
    return [
        {
            account_number: legNumber,
            account_name: accountNameFor(legNumber)
                || (legNumber === String(defaultLegAccount.number) ? defaultLegAccount.name : ''),
            debit: incoming ? amount : '',
            credit: incoming ? '' : amount,
            description: suggestion.description || null,
        },
        {
            account_number: counterNumber,
            account_name: counterNumber ? accountNameFor(counterNumber) : '',
            debit: incoming ? '' : amount,
            credit: incoming ? amount : '',
            description: suggestion.description || null,
        },
    ];
}

export function bookingSuggestionTitle(suggestion) {
    if (!suggestion) return 'Konteringsförslag';
    if (suggestion.title) return suggestion.title;
    if (suggestion.ruleName) return suggestion.ruleName;
    if (suggestion.description) return suggestion.description;
    if (suggestion.counterAccount) return `Konto ${suggestion.counterAccount}`;
    if (suggestion.debitAccount && suggestion.creditAccount) {
        return `${suggestion.debitAccount} / ${suggestion.creditAccount}`;
    }
    return 'Konteringsförslag';
}
